import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../i18n";
import { Button } from "./ui/button";
import { Menu, X } from "lucide-react";
import { NotificationBell } from "./NotificationBell";

export const Navbar = () => {
  const { user, logout } = useAuth();
  const { t, lang, toggle } = useLang();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const isAuthed = user && user !== false;
  const close = () => setMenuOpen(false);

  const langBtn = (extra = "") => (
    <button data-testid="lang-toggle-btn" onClick={toggle}
      className={`text-xs font-bold uppercase tracking-widest text-zinc-400 hover:text-white transition-colors border border-zinc-700 rounded-full px-3 py-1.5 ${extra}`}>
      {lang === "en" ? "ES" : "EN"}
    </button>
  );

  return (
    <nav className="glass-nav sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-2" onClick={close}>
          <img src="/ojosq-logo.png" alt="StreameyeQ" className="w-9 h-9 object-contain" />
          <span className="font-display font-black text-xl tracking-tight text-white">StreameyeQ</span>
        </Link>

        {/* Desktop */}
        <div className="hidden md:flex items-center gap-3">
          <Link to="/descubrir" data-testid="nav-discover-link" className="text-sm text-zinc-400 hover:text-white transition-colors px-3">
            {t("nav_discover")}
          </Link>
          <Link to="/debates" data-testid="nav-debates-link" className="text-sm text-zinc-400 hover:text-white transition-colors px-3">
            {t("nav_debates")}
          </Link>
          {isAuthed && user.role === "admin" && (
            <Link to="/admin" data-testid="nav-admin-link" className="text-sm text-emerald-400 hover:text-emerald-300 transition-colors px-3 font-semibold">
              {t("nav_admin")}
            </Link>
          )}
          {isAuthed ? (
            <>
              <Link to="/dashboard" data-testid="nav-dashboard-link" className="text-sm text-zinc-400 hover:text-white transition-colors px-3">
                {t("nav_dashboard")}
              </Link>
              <NotificationBell />
              <span data-testid="nav-user-name" className="text-sm text-zinc-500 hidden lg:block">{user.name}</span>
              <Button data-testid="nav-logout-btn" variant="outline" size="sm" className="rounded-full border-zinc-700 text-zinc-300 hover:bg-zinc-800 hover:text-white bg-transparent"
                onClick={async () => { await logout(); navigate("/"); }}>
                {t("nav_logout")}
              </Button>
            </>
          ) : (
            <Button data-testid="nav-login-btn" size="sm" className="rounded-full bg-emerald-600 hover:bg-emerald-500 text-white"
              onClick={() => navigate("/auth")}>
              {t("nav_login")}
            </Button>
          )}
          {langBtn()}
        </div>

        {/* Mobile */}
        <div className="flex md:hidden items-center gap-2">
          {isAuthed && <NotificationBell testIdSuffix="-mobile" />}
          <button data-testid="nav-mobile-toggle" onClick={() => setMenuOpen((o) => !o)} aria-label="Menu"
            className="text-zinc-300 hover:text-white transition-colors p-1.5">
            {menuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile menu panel */}
      {menuOpen && (
        <div data-testid="nav-mobile-menu" className="md:hidden border-t border-zinc-800 bg-zinc-950/95 backdrop-blur px-6 py-4 flex flex-col gap-1">
          <Link to="/descubrir" onClick={close} className="text-sm text-zinc-300 hover:text-white transition-colors py-2.5">{t("nav_discover")}</Link>
          <Link to="/debates" onClick={close} className="text-sm text-zinc-300 hover:text-white transition-colors py-2.5">{t("nav_debates")}</Link>
          {isAuthed && user.role === "admin" && (
            <Link to="/admin" onClick={close} className="text-sm text-emerald-400 hover:text-emerald-300 transition-colors py-2.5 font-semibold">{t("nav_admin")}</Link>
          )}
          {isAuthed && (
            <Link to="/dashboard" onClick={close} className="text-sm text-zinc-300 hover:text-white transition-colors py-2.5">{t("nav_dashboard")}</Link>
          )}
          <div className="flex items-center justify-between pt-3 mt-2 border-t border-zinc-800">
            {isAuthed ? (
              <>
                <span className="text-sm text-zinc-500">{user.name}</span>
                <Button data-testid="nav-logout-btn-mobile" variant="outline" size="sm" className="rounded-full border-zinc-700 text-zinc-300 hover:bg-zinc-800 hover:text-white bg-transparent"
                  onClick={async () => { close(); await logout(); navigate("/"); }}>
                  {t("nav_logout")}
                </Button>
              </>
            ) : (
              <Button data-testid="nav-login-btn-mobile" size="sm" className="rounded-full bg-emerald-600 hover:bg-emerald-500 text-white"
                onClick={() => { close(); navigate("/auth"); }}>
                {t("nav_login")}
              </Button>
            )}
            {langBtn()}
          </div>
        </div>
      )}
    </nav>
  );
};
